import React from 'react';
import {t} from 'ttag';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography
} from '@mui/material';
import {styles} from '@invoice-system';
import {Check, Close, WarningAmber} from '@mui/icons-material';
import {IInvoice} from '../../../../store/apis/interfaces/invoice';
import {useEditInvoiceMutation} from '../../../../store/apis/ccs';
import {useAccount} from '@azure/msal-react';
import {enqueueSnackbar} from 'notistack';
import RTKQueryFeedback from '@components/common/RTKQueryFeedback';

interface ConfirmationEditDialogProps {
  data: IInvoice;
  formData?: any;
  onClose: () => void;
}

export const ConfirmationEditDialog = ({data, formData, onClose}: ConfirmationEditDialogProps) => {
  const [editInvoice] = useEditInvoiceMutation();
  const [isLoading, setIsLoading] = React.useState(false);
  const account = useAccount();

  const handleEdit = () => {
    setIsLoading(true);
    editInvoice({
      ticket: data?.ticket,
      employeeId: account?.username?.split('@')[0] as string,
      email: formData?.email,
      address: formData?.address,
      postalCode: formData?.postalCode,
      city: formData?.city,
      country: formData?.country,
      billingName: formData?.billingName,
      taxId: formData?.taxId,
    }).then((response: any) => {
      if (!response.error) {
        enqueueSnackbar(t`Invoice revised and updated successfully.`, {variant: 'success'});
        setTimeout(() => {
          window.location.reload();
        }, 200);
      }
      else {
        setIsLoading(false);
        onClose();
        enqueueSnackbar(response.error?.message, {variant: 'error', autoHideDuration: null});
      }
    });
  }

  if (Number(data?.sequence || 0) > 1) {
    return (
      <Dialog
        open={true}
        maxWidth={'sm'}
        slotProps={{
          backdrop: {
            style: {backgroundColor: 'black', opacity: 0.75}
          }
        }}
        transitionDuration={350}
        fullWidth
        onClose={onClose}
      >
        <DialogTitle>
          <Box display={'flex'} justifyContent={'space-between'} alignItems={'start'}>
            <Box display={'flex'} gap={0.75} alignItems={'center'}>
              <WarningAmber color={'warning'} fontSize={'small'} />
              <Typography sx={styles.subtitle}>{t`Revision Limit Reached`}</Typography>
            </Box>
            <IconButton onClick={onClose} size={'small'} sx={{height: '30px', width: '30px'}}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Typography variant={'body2'} fontSize={'0.975rem'} color={'text.secondary'}>
            {t`You have reached the maximum number of revisions for this invoice.`}
          </Typography>
        </DialogContent>
        <Divider />
        <DialogActions sx={{justifyContent: 'flex-end', px: 3, py: 1.5}}>
          <Button variant={'outlined'} onClick={onClose}>{t`Close`}</Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={true}
      maxWidth={'sm'}
      slotProps={{
        backdrop: {
          style: {backgroundColor: 'black', opacity: 0.75}
        }
      }}
      transitionDuration={350}
      fullWidth
      onClose={onClose}
    >
      <DialogTitle>
        <Box display={'flex'} justifyContent={'space-between'} alignItems={'start'}>
          <Box display={'flex'} gap={0.75} alignItems={'center'}>
            <WarningAmber color={'warning'} fontSize={'small'} />
            <Typography sx={styles.subtitle}>{t`Confirmation Required`}</Typography>
          </Box>
          <IconButton onClick={onClose} size={'small'} sx={{height: '30px', width: '30px'}}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Box display={'flex'} flexDirection={'column'} gap={1}>
          <Typography variant={'body2'} fontSize={'0.975rem'} color={'text.secondary'}>
            {t`This will create a `}
            <b style={{ fontFamily: 'OpenSansMedium' }}>{t`new revision`}</b>
            {t` of the invoice and the current invoice will be marked as cancelled.`}
          </Typography>
          <Typography variant={'body2'} fontSize={'0.975rem'} color={'text.secondary'}>
            {t`You can only create up to 2 revisions per invoice. This will be revision` + ` ${Number(data?.sequence || 0) + 1}.`}
          </Typography>
        </Box>
      </DialogContent>
      <Divider />
      <DialogActions sx={styles.dialogActions}>
        <Button variant={'outlined'} sx={styles.dialogButton} onClick={onClose}>{t`Cancel`}</Button>
        <Button variant={'contained'} sx={styles.dialogButton} color={'warning'} startIcon={<Check/>} onClick={handleEdit}>{t`Create Revised Invoice`}</Button>
      </DialogActions>
      <RTKQueryFeedback isLoading={isLoading}/>
    </Dialog>
  );
};

export default ConfirmationEditDialog;
